import { EventClickArg, EventContentArg, EventInput } from '@fullcalendar/core';
import { EventImpl } from '@fullcalendar/core/internal';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import FullCalendar from '@fullcalendar/react';
import { ResourceInput } from '@fullcalendar/resource';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import scrollgridPlugin from '@fullcalendar/scrollgrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { AppointmentListItem } from '@spike/appointments-list-model';
import { PageResult } from '@spike/model';
import { Room } from '@spike/room-model';
import { BoardingDayHeader } from 'components/Calendar3/Boarding/DayHeader/DayHeader';
import { EventCell } from 'components/Calendar3/Boarding/EventCell/EventCell';
import { UnitLabel } from 'components/Calendar3/Boarding/UnitLabel/UnitLabel';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from 'store';
import styles from './WeekFullCalendar.module.scss';

export type AppointmentEvent = Omit<EventInput, 'extendedProps'> & {
    extendedProps: {
        appointment: AppointmentListItem;
    };
};

export type AppointmentEventContentArg = Omit<EventContentArg, 'event'> & {
    event: Omit<EventImpl, 'extendedProps'> & {
        extendedProps: {
            appointment: AppointmentListItem;
        };
    };
};

interface BoardingWeekFullCalendarProps {
    date: Moment;
    timeZone: string;
    selectedRoomUnitIds: Array<number> | undefined;
    readOnly: boolean;
    onClickAppointment?: (id: number) => void;
    onBook?: (date: Moment) => void;
}

const getUnassignedResourceId = (room: Room) => `unassigned-${(room as Room & { id?: number }).id ?? room.name ?? ''}`;

export const BoardingWeekFullCalendar: React.FC<BoardingWeekFullCalendarProps> = ({
    date,
    timeZone,
    selectedRoomUnitIds,
    readOnly,
    onClickAppointment,
    onBook
}) => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const today = moment().tz(timeZone);
    const scrollTime = today.format('HH:00:00');

    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);
    const appointments = useSelector<RootState, Array<AppointmentListItem>>(
        state => state.appointmentsList.boardings.list
    );

    const fullCalendarRef = React.createRef<FullCalendar>();
    const getCalendarNow = React.useCallback(() => moment.tz(timeZone).format(), [timeZone]);

    const appointmentsEvents = React.useMemo(() => {
        return appointments.map(appointment => {
            const unitId = appointment.room?.unit?.id;
            let resourceId: string | undefined;

            if (unitId != null) {
                resourceId = unitId.toString();
            } else if (appointment.room) {
                const roomFromList = rooms.page.find(
                    r =>
                        (r as Room & { id?: number }).id === (appointment.room as { id?: number })?.id ||
                        r.name === (appointment.room as { name?: string })?.name
                );
                resourceId = roomFromList ? getUnassignedResourceId(roomFromList) : undefined;
            } else {
                const boardingRoomId = (appointment as { boarding?: { room?: number } }).boarding?.room;
                if (boardingRoomId != null) {
                    const roomFromList = rooms.page.find(
                        r =>
                            (r as Room & { id?: number }).id === boardingRoomId ||
                            String((r as Room & { id?: number }).id) === String(boardingRoomId)
                    );
                    resourceId = roomFromList ? getUnassignedResourceId(roomFromList) : undefined;
                } else {
                    resourceId = undefined;
                }
            }

            return {
                id: appointment.appointmentId.toString(),
                start: appointment.startAt.toDate(),
                end: appointment.endAt.toDate(),
                title: appointment.pet.name,
                resourceId,
                extendedProps: {
                    appointment: appointment
                }
            } as AppointmentEvent;
        });
    }, [appointments, rooms.page]);

    const eventClickHandler = (args: EventClickArg) => {
        onClickAppointment?.(args.event.extendedProps.appointment.appointmentId);
    };

    const dateClickHandler = (args: DateClickArg) => {
        const at = moment(args.date).tz(timeZone);
        const quarter = Math.trunc(at.minutes() / 15);
        at.minutes(quarter * 15);
        onBook?.(at);
    };

    const fullCalendarComponent = React.useMemo(() => {
        const resources = rooms.page.reduce((items, room) => {
            if (selectedRoomUnitIds === undefined || selectedRoomUnitIds.length > 0) {
                items.push({
                    title: 'Unassigned',
                    id: getUnassignedResourceId(room),
                    roomId: room.name?.toString(),
                    order: 0,
                    extendedProps: {
                        room,
                        isUnassigned: true
                    }
                });
            }

            room.units.forEach(unit => {
                if (selectedRoomUnitIds && !selectedRoomUnitIds.includes(unit.id!)) return items;

                const isUnavailable = !unit.active;

                items.push({
                    title: unit.name,
                    id: unit.id?.toString(),
                    roomId: room.name?.toString(),
                    order: 1,
                    extendedProps: {
                        room,
                        isUnavailable
                    }
                });
            });

            return items;
        }, [] as Array<ResourceInput>);

        return (
            <FullCalendar
                ref={fullCalendarRef}
                timeZone={timeZone}
                now={getCalendarNow}
                scrollTime={scrollTime}
                initialView="resourceTimelineWeek"
                height="100%"
                headerToolbar={false}
                stickyHeaderDates={false}
                slotMinWidth={150}
                plugins={[
                    interactionPlugin,
                    momentTimezonePlugin,
                    timeGridPlugin,
                    scrollgridPlugin,
                    resourceTimelinePlugin
                ].filter(p => !readOnly || p.name !== interactionPlugin.name)}
                slotDuration={{ day: 1 }}
                slotLabelContent={props => (
                    <BoardingDayHeader
                        {...props}
                        date={moment(props.date).tz(timeZone)}
                        timeZone={timeZone}
                    />
                )}
                // Resources
                resources={resources}
                resourceAreaWidth={isMobile ? 150 : 200}
                resourceGroupField="roomId"
                resourceOrder="order"
                resourceAreaHeaderContent={''}
                resourceLabelContent={props => <UnitLabel {...props} />}
                resourceLabelClassNames={args => [
                    styles.resourceLabel,
                    ...(args.resource.extendedProps?.isUnavailable ? [styles.disabledLabel] : [])
                ]}
                resourceGroupLabelClassNames={styles.resourceGroupLabel}
                resourceLaneClassNames={args =>
                    args.resource.extendedProps?.isUnavailable ? [styles.disabledRow] : []
                }
                // Events
                events={appointmentsEvents}
                eventClick={eventClickHandler}
                eventContent={(props: AppointmentEventContentArg) => (
                    <EventCell
                        {...props}
                        timeZone={timeZone}
                    />
                )}
                // Date click
                dateClick={dateClickHandler}
            />
        );
    }, [rooms, selectedRoomUnitIds, timeZone, scrollTime, date, isMobile, appointmentsEvents, getCalendarNow]);

    React.useEffect(() => {
        fullCalendarRef.current?.getApi().gotoDate(date.toDate());
    }, [date]);

    return <div className={styles.root}>{fullCalendarComponent}</div>;
};
